<script setup lang="ts">
/**
 * The one nudge toward a strong sign-in method, on Home. Shown to a guest with no passkey here and no authenticator
 * app; "Not now" hides it on this browser for thirty days. Draws nothing otherwise.
 *
 * @changelog
 *
 * 2026-10-10 (0.1.0) (jhoward)
 *   - Add PasskeyNudge.vue, replacing the "Let's Secure Your Account!" block that had no link and, since a verified
 *     email counts as an enrolled method, almost never showed.
 *
 * 2026-10-21 (0.2.0) (jhoward)
 *   - Add an "Add an authenticator app" big link beside "Add a passkey" when the browser supports passkeys.
 *   - Link each button to its own section of the security page (#passkeys, #authenticator).
 *
 * 2026-10-23 (0.3.0) (jhoward)
 *   - Offer a passkey only where canUsePasskeysHere() says this browser can use one on this domain.
 */

import PeppermillContentBlock from '@framework/components/layout/PeppermillContentBlock.vue';
import PeppermillBigLink from '@framework/components/PeppermillBigLink.vue';
import { computed, onMounted, ref } from 'vue';
import type { User } from '@/defs/user';
import { canUsePasskeysHere } from '@/helpers/webAuthnSupport';
import useAppStore from '@/store';

const DISMISSED_KEY = 'opc.passkey_nudge_dismissed_at';
const DISMISSED_FOR_MS = 30 * 24 * 60 * 60 * 1000;

const appStore = useAppStore();
const isDismissed = ref<boolean>(true);
const canUsePasskey = ref<boolean>(false);

const user = computed<User | null>(() => appStore.state.user as User | null);
const isShown = computed<boolean>(() => !!user.value && true !== user.value.has_strong_two_factor && !isDismissed.value);
const daysStandard = computed<number>(() => appStore.remembered.days_standard ?? 30);
const daysStrong = computed<number>(() => appStore.remembered.days_strong ?? 90);

//Browser storage can be missing or throw (private windows, cleared site data); the nudge then simply shows.
function readDismissedAt(): number | null {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    return raw ? Number(raw) : null;
  } catch {
    return null;
  }
}

function dismiss(): void {
  isDismissed.value = true;
  try {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
  } catch {
    //Then it shows again next visit, which is the lesser harm.
  }
}

onMounted(async () => {
  canUsePasskey.value = await canUsePasskeysHere();
  const dismissedAt = readDismissedAt();
  isDismissed.value = null !== dismissedAt && Date.now() - dismissedAt < DISMISSED_FOR_MS;
});
</script>

<template>
  <template v-if="isShown">
    <h2>{{ canUsePasskey ? 'Set up a passkey' : 'Add an authenticator app' }}</h2>
    <peppermill-content-block>
      <p v-if="canUsePasskey">
        Sign in with Face ID, Touch ID, Windows Hello, or a security key &mdash; no code to type. Passkey users stay
        signed in on a remembered device for {{ daysStrong }} days instead of {{ daysStandard }}.
      </p>
      <p v-else>
        This browser can't use passkeys on this site. An authenticator app gives you the same protection and the same
        {{ daysStrong }}-day remembered-device window.
      </p>
      <p><a href="#" @click.prevent="dismiss">Not now</a></p>
    </peppermill-content-block>
    <peppermill-content-block is-big-link-container>
      <peppermill-big-link
        :path="canUsePasskey ? '/account/security#passkeys' : '/account/security#authenticator'"
        :title="canUsePasskey ? 'Add a passkey' : 'Set up an authenticator app'"
        :icon="canUsePasskey ? 'key' : 'phonelink_lock'"
      ></peppermill-big-link>
      <peppermill-big-link
        v-if="canUsePasskey"
        path="/account/security#authenticator"
        title="Add an authenticator app"
        icon="phonelink_lock"
      ></peppermill-big-link>
    </peppermill-content-block>
  </template>
</template>
